import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { gsap, ScrollTrigger } from '@/lib/gsap'
import DeckleImage from '@/components/generals/deckle-image'
import Reveal from '@/components/generals/reveal'
import { PROCESS_STEPS } from '@/constants/content'

// The five steps told as one scroll: on wide screens the photograph stays put and changes
// as each step passes the middle of the screen, while a pomegranate thread fills alongside.
const ProcessStory: FC = (): ReactNode => {
    const [active, setActive] = useState(0)
    const listRef = useRef<HTMLOListElement | null>(null)
    const fillRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        const list = listRef.current
        if (!list) return

        const items = Array.from(list.querySelectorAll<HTMLElement>('[data-step-index]'))

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) setActive(Number(entry.target.getAttribute('data-step-index')))
                })
            },
            { rootMargin: '-48% 0px -48% 0px', threshold: 0 }
        )

        items.forEach(item => observer.observe(item))
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        const list = listRef.current
        const fill = fillRef.current
        if (!list || !fill) return

        const context = gsap.context(() => {
            ScrollTrigger.create({
                trigger: list,
                start: 'top 50%',
                end: 'bottom 50%',
                onUpdate: (self) => {
                    fill.style.transform = `scaleY(${self.progress.toFixed(4)})`
                }
            })
        })

        const refresh = (): void => ScrollTrigger.refresh()
        window.addEventListener('load', refresh)

        return () => {
            window.removeEventListener('load', refresh)
            context.revert()
        }
    }, [])

    const current = PROCESS_STEPS[active]

    return (
        <section id='process' aria-labelledby='process-title' className='relative bg-paper-deep/55 py-20 md:py-32'>
            <div className='container-page grid gap-8 lg:grid-cols-12'>
                <Reveal className='lg:col-span-4'>
                    <p className='flex items-center gap-4 text-sm font-medium text-bark-soft'>
                        <span aria-hidden='true' className='h-px w-10 bg-pomegranate' />
                        The making
                    </p>
                </Reveal>
                <Reveal delay={120} className='lg:col-span-7 lg:col-start-6'>
                    <h2 id='process-title' className='font-heading text-[clamp(2.3rem,5vw,4.4rem)] font-light leading-[1.02] tracking-[-0.02em] text-bark'>
                        From branch to sheet, in five slow steps.
                    </h2>
                    <p className='mt-6 max-w-xl text-[1.05rem] leading-relaxed text-bark-soft'>
                        Nothing here is hurried and nothing is bleached. It takes about three weeks for a mulberry branch to become a sheet you can write on.
                    </p>
                </Reveal>
            </div>

            <div className='container-page mt-14 lg:mt-24 lg:grid lg:grid-cols-12 lg:gap-8'>
                <div className='hidden lg:col-span-6 lg:block'>
                    <div className='sticky top-28'>
                        <div className='paper-lift w-[min(100%,calc((100svh-12rem)*0.8))]'>
                            <div className='deckle relative aspect-[4/5] overflow-hidden bg-paper-shade'>
                                {PROCESS_STEPS.map((step, index) => (
                                    <div key={step.id} data-active={index === active} className='story-sheet absolute inset-0'>
                                        <Image
                                            src={step.image}
                                            alt={step.alt}
                                            fill
                                            sizes='(min-width: 1360px) 620px, 45vw'
                                            quality={82}
                                            className='object-cover'
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div aria-live='polite' className='mt-6 flex w-[min(100%,calc((100svh-12rem)*0.8))] items-baseline justify-between gap-6 text-sm'>
                            <p className='text-bark-soft'>
                                {current.timing}
                            </p>
                            <p className='font-medium tabular-nums text-bark'>
                                Step {active + 1} of {PROCESS_STEPS.length}
                            </p>
                        </div>
                    </div>
                </div>

                <ol ref={listRef} className='relative lg:col-span-5 lg:col-start-8 lg:pb-[16svh]'>
                    <div aria-hidden='true' className='absolute bottom-[16svh] left-0 top-2.5 hidden w-px bg-bark/15 lg:block'>
                        <div ref={fillRef} className='story-thread-fill absolute inset-0 bg-pomegranate' />
                    </div>

                    {PROCESS_STEPS.map((step, index) => (
                        <li
                            key={step.id}
                            data-step-index={index}
                            className='relative border-b border-bark/12 py-12 first:pt-0 last:border-0 lg:border-0 lg:pb-[180px] lg:pl-14 lg:pt-0 lg:last:pb-0'
                        >
                            <DeckleImage
                                src={step.image}
                                alt={step.alt}
                                sizes='(min-width: 640px) 70vw, 92vw'
                                className='mb-8 sm:w-[78%] lg:hidden'
                            />

                            <div className='relative'>
                                <span
                                    aria-hidden='true'
                                    className={cn(
                                        'absolute -left-[calc(3.5rem+4.5px)] top-[5px] hidden size-2.5 rounded-full border transition-colors duration-700 lg:block',
                                        index <= active ? 'border-pomegranate bg-pomegranate' : 'border-bark/30 bg-paper'
                                    )}
                                />

                                <p className='flex items-baseline gap-4 text-sm'>
                                    <span className='font-semibold tabular-nums text-pomegranate'>{step.number}</span>
                                    <span className='text-bark-soft'>{step.timing}</span>
                                </p>
                                <h3 className='mt-4 font-heading text-[clamp(1.9rem,3vw,2.6rem)] font-light leading-[1.05] text-bark'>
                                    {step.title}
                                </h3>
                                <p className='mt-5 max-w-[34rem] text-[1.05rem] leading-[1.75] text-bark-soft'>
                                    {step.text}
                                </p>
                            </div>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    )
}

export default ProcessStory
